
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatBraceletCode } from "@/utils/braceletUtils";

interface ChurchProfileSectionProps {
  congregationName: string;
  logo: string | null;
  churchCode?: string;
  isReadOnly: boolean;
  onNameChange: (name: string) => void;
  onLogoChange: (logo: string | null) => void;
  onSave: () => Promise<boolean>;
}

export function ChurchProfileSection({
  congregationName,
  logo,
  churchCode,
  isReadOnly,
  onNameChange,
  onLogoChange,
  onSave,
}: ChurchProfileSectionProps) {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState<boolean>(false);
  const [braceletCodes, setBraceletCodes] = useState<string[]>([]);
  const [isLoadingBracelets, setIsLoadingBracelets] = useState<boolean>(false);

  // Fetch existing bracelet codes for this church on mount
  useEffect(() => {
    if (churchCode) {
      fetchBraceletCodes();
    }
  }, [churchCode]);

  const fetchBraceletCodes = async () => {
    if (!churchCode) return;
    
    setIsLoadingBracelets(true);
    try {
      const { data, error } = await supabase
        .from("bracelets")
        .select("referral_code")
        .eq("church_code", churchCode);
        
      if (error) throw error;
      
      setBraceletCodes((data || []).map(b => b.referral_code));
    } catch (error: any) {
      console.error("Error fetching bracelet codes:", error);
      toast({
        title: "Error",
        description: "Failed to load associated bracelet codes",
        variant: "destructive",
      });
    } finally {
      setIsLoadingBracelets(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const file = e.target.files[0];
    setIsUploading(true);
    
    try {
      // Create a unique file name for the logo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `${churchCode || 'default'}/${fileName}`;
      
      console.log("Uploading logo to church-logos bucket with path:", filePath);
      
      // Use the new 'church-logos' bucket instead of 'avatars'
      const { error: uploadError, data } = await supabase.storage
        .from('church-logos')
        .upload(filePath, file);
        
      if (uploadError) {
        console.error("Upload error:", uploadError);
        throw uploadError;
      }
      
      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('church-logos')
        .getPublicUrl(filePath);
      
      console.log("Logo uploaded successfully:", publicUrl);
      
      // Update the logo URL
      onLogoChange(publicUrl);
      
      toast({
        title: "Success",
        description: "Church logo uploaded successfully",
      });
    } catch (error: any) {
      console.error("Error uploading logo:", error);
      toast({
        title: "Error",
        description: "Failed to upload logo: " + error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onNameChange(e.target.value);
  };

  const handleSaveConfirm = async () => {
    const success = await onSave();
    setShowSaveConfirm(false);
    if (success) {
      toast({
        title: "Success",
        description: "Congregation settings saved successfully",
      });
    }
  };

  return (
    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
      <h3 className="font-medium text-red-800 mb-3">Church Profile</h3>
      
      <div className="space-y-4">
        <div className="bg-white p-3 rounded border border-red-100">
          <Label htmlFor="churchCode" className="text-red-900">Church Code</Label>
          <div className="font-mono text-2xl mt-1 text-center py-2 bg-gray-50 rounded border border-gray-200">
            {churchCode || "000"}
          </div>
          <p className="text-xs text-app-text-secondary mt-1">
            This unique code identifies your church in our system and is used to associate bracelets with your church.
          </p>
        </div>
      
        <div>
          <Label htmlFor="churchName" className="text-red-900">Congregation Name</Label>
          <Input 
            id="churchName" 
            value={congregationName} 
            onChange={!isReadOnly ? handleNameChange : undefined}
            className="mt-1"
            readOnly={isReadOnly}
            disabled={isReadOnly}
          />
          {isReadOnly && (
            <p className="text-xs text-app-text-secondary mt-1">
              This congregation name is associated with a specific referral code.
            </p>
          )}
        </div>
        
        <div>
          <Label className="text-red-900 block mb-2">Church Logo</Label>
          {logo && (
            <div className="mb-3 flex justify-center">
              <img 
                src={logo} 
                alt="Church Logo" 
                className="w-24 h-24 object-contain border border-gray-200 rounded"
              />
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              className="relative" 
              disabled={isUploading || isReadOnly}
            >
              <input 
                type="file" 
                className="absolute inset-0 opacity-0 cursor-pointer" 
                onChange={handleLogoUpload}
                accept="image/*"
                disabled={isReadOnly}
              />
              <Upload className="w-4 h-4 mr-2" />
              {isUploading ? "Uploading..." : "Upload Logo"}
            </Button>
            {logo && !isReadOnly && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => onLogoChange(null)}
                className="text-red-500 hover:text-red-700"
              >
                Remove
              </Button>
            )}
          </div>
          {isReadOnly && (
            <p className="text-xs text-app-text-secondary mt-1">
              This church logo is associated with a specific referral code.
            </p>
          )}
        </div>

        <div className="bg-white p-3 rounded border border-red-100">
          <div className="flex justify-between items-center mb-2">
            <Label className="text-red-900">Associated Bracelets</Label>
          </div>
          
          {isLoadingBracelets ? (
            <div className="py-3 text-center text-app-text-secondary">Loading bracelets...</div>
          ) : braceletCodes.length === 0 ? (
            <div className="py-3 text-center text-app-text-secondary">No bracelets associated yet</div>
          ) : (
            <ScrollArea className="h-48 border rounded">
              <div className="p-2">
                {braceletCodes.map((code) => (
                  <div 
                    key={code} 
                    className="flex justify-between items-center p-2 hover:bg-gray-50 border-b last:border-b-0"
                  >
                    <span className="font-mono">{formatBraceletCode(code)}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
          <p className="text-xs text-app-text-secondary mt-1">
            These bracelets are linked to your church and will display your logo and name when scanned.
            <br />
            Only bracelet codes specifically assigned to your church by the system administrator will appear here.
          </p>
        </div>
        
        {!isReadOnly && (
          <Button 
            onClick={() => setShowSaveConfirm(true)}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            Save Church Settings
          </Button>
        )}
      </div>

      <AlertDialog open={showSaveConfirm} onOpenChange={setShowSaveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Church Settings</AlertDialogTitle>
            <AlertDialogDescription>
              This will update your congregation name and logo throughout the system.
              These changes will be visible to all your church members and any users
              who access your church through bracelets or referral codes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSaveConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Save Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
